import * as i0 from '@angular/core';
import { Directive, Input, NgModule } from '@angular/core';
import { fromEvent } from 'rxjs';
import { tap, switchMap, takeUntil, pairwise } from 'rxjs/operators';
import * as i1 from '@angular/material/tabs';

class MatTabGroupGestureDirective {
    constructor(tabGroup) {
        this.tabGroup = tabGroup;
        this.skipBodySwipe = false;
        this.swipeLimitWidth = 80;
        this.connectEdges = true;
    }
    ngOnInit() {
        this.headers = this.tabGroup._elementRef.nativeElement.querySelector('mat-tab-header');
        if (!this.headers) {
            throw new Error('No headers found in DOM! Aborting...');
        }
        this.headersList = this.headers.querySelector('.mat-tab-list');
        if (!this.headersList) {
            throw new Error('No headers list found in DOM! Aborting...');
        }
        this.body = this.tabGroup._elementRef.nativeElement.querySelector('.mat-tab-body-wrapper');
        if (!this.body) {
            throw new Error('No body found in DOM! Aborting...');
        }
        this._handleHeadersEvents();
        this._handleBodyEvents();
    }
    _handleHeadersEvents() {
        // this will capture all touchstart events from the headers element
        fromEvent(this.headers, 'touchstart')
            .pipe(tap(() => {
            this.originalHeadersListTransition = this.headersList.style.transition;
            this.headersList.style.transition = 'none';
            this.headersMaxScroll = -1 * (this.headersList.offsetWidth - this.headers.offsetWidth + 64);
        }), switchMap((e) => {
            // after a mouse down, we'll record all mouse moves
            return fromEvent(this.headers, 'touchmove')
                .pipe(
            // we'll stop (and unsubscribe) once the user releases the mouse
            // this will trigger a 'mouseup' event
            takeUntil(fromEvent(this.headers, 'touchend').pipe(tap(() => this.headersList.style.transition = this.originalHeadersListTransition))), 
            // pairwise lets us get the previous value to draw a line from
            // the previous point to the current point
            pairwise());
        }))
            .subscribe((res) => {
            const rect = this.headers.getBoundingClientRect();
            // previous and current position with the offset
            const prevX = res[0].touches[0].clientX - rect.left;
            const currentX = res[1].touches[0].clientX - rect.left;
            this._scrollHeaders(currentX - prevX);
        });
    }
    _scrollHeaders(scrollX) {
        if (!this.headersList || !this.headersMaxScroll) {
            return;
        }
        const currentTransform = this.headersList.style.transform;
        let currentScroll;
        if (currentTransform && currentTransform.indexOf('translateX') > -1) {
            let tmp = currentTransform.substring('translateX('.length);
            tmp = tmp.substring(0, tmp.length - 'px)'.length);
            currentScroll = parseInt(tmp, 10);
        }
        else {
            currentScroll = 0;
        }
        let newScroll = currentScroll + scrollX;
        if (newScroll > 0) {
            newScroll = 0;
        }
        if (newScroll < this.headersMaxScroll) {
            newScroll = this.headersMaxScroll;
        }
        // this._renderer.setStyle(this._headersList, 'transform', `translateX(${newScroll}px)`);
        this.headersList.style.transform = `translateX(${newScroll}px)`;
    }
    _handleBodyEvents() {
        // this will capture all touchstart events from the headers element
        fromEvent(this.body, 'touchstart')
            .pipe(switchMap((e) => {
            // need to test classname to string otherwise can throw error
            if (e.path.findIndex((p) => p.className && typeof p.className === 'string' && p.className.indexOf('mat-slider') > -1) > -1) {
                this.skipBodySwipe = true;
            }
            // after a mouse down, we'll record all mouse moves
            return fromEvent(this.body, 'touchmove')
                .pipe(
            // we'll stop (and unsubscribe) once the user releases the mouse
            // this will trigger a 'mouseup' event
            takeUntil(fromEvent(this.body, 'touchend').pipe(tap(() => {
                this.skipBodySwipe = false;
                if (!this.bodyCurrentScroll) {
                    return;
                }
                if (Math.abs(this.bodyCurrentScroll.y) > Math.abs(this.bodyCurrentScroll.x)) {
                    return;
                }
                const limitPrev = this.swipeLimitWidth;
                const limitNext = -1 * this.swipeLimitWidth;
                if (this.bodyCurrentScroll.x > limitPrev && this.bodyCurrentScroll.x < limitNext) {
                    return;
                }
                if (this.bodyCurrentScroll.x > limitPrev) {
                    this._prevTab();
                }
                else if (this.bodyCurrentScroll.x < limitNext) {
                    this._nextTab();
                }
                this.bodyCurrentScroll = { x: 0, y: 0 };
            }))), 
            // pairwise lets us get the previous value to draw a line from
            // the previous point to the current point
            pairwise());
        }))
            .subscribe((res) => {
            if (this.skipBodySwipe) {
                return;
            }
            const rect = this.body.getBoundingClientRect();
            // previous and current position with the offset
            const prevPos = {
                x: res[0].touches[0].clientX - rect.left,
                y: res[0].touches[0].clientY - rect.top
            };
            const currentPos = {
                x: res[1].touches[0].clientX - rect.left,
                y: res[1].touches[0].clientY - rect.top
            };
            if (!this.bodyCurrentScroll) {
                this.bodyCurrentScroll = { x: 0, y: 0 };
            }
            this.bodyCurrentScroll = {
                x: this.bodyCurrentScroll.x + currentPos.x - prevPos.x,
                y: this.bodyCurrentScroll.y + currentPos.y - prevPos.y,
            };
        });
    }
    _prevTab() {
        if (this.tabGroup.selectedIndex === 0 || this.tabGroup.selectedIndex === null) {
            this.tabGroup.selectedIndex = this.connectEdges ? this.tabGroup._tabs.length - 1 : this.tabGroup.selectedIndex;
        }
        else {
            this.tabGroup.selectedIndex = this.tabGroup.selectedIndex - 1;
        }
    }
    _nextTab() {
        if (this.tabGroup.selectedIndex === this.tabGroup._tabs.length - 1) {
            this.tabGroup.selectedIndex = this.connectEdges ? 0 : this.tabGroup.selectedIndex;
        }
        else if (this.tabGroup.selectedIndex === null) {
            this.tabGroup.selectedIndex = 0;
        }
        else {
            this.tabGroup.selectedIndex = this.tabGroup.selectedIndex + 1;
        }
    }
}
MatTabGroupGestureDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MatTabGroupGestureDirective, deps: [{ token: i1.MatTabGroup }], target: i0.ɵɵFactoryTarget.Directive });
MatTabGroupGestureDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.12", type: MatTabGroupGestureDirective, selector: "mat-tab-group[matTabGroupGesture]", inputs: { swipeLimitWidth: "swipeLimitWidth", connectEdges: "connectEdges" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MatTabGroupGestureDirective, decorators: [{
            type: Directive,
            args: [{
                    // tslint:disable-next-line:directive-selector
                    selector: 'mat-tab-group[matTabGroupGesture]'
                }]
        }], ctorParameters: function () { return [{ type: i1.MatTabGroup }]; }, propDecorators: { swipeLimitWidth: [{
                type: Input
            }], connectEdges: [{
                type: Input
            }] } });

class MatTabGroupGestureModule {
}
MatTabGroupGestureModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MatTabGroupGestureModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MatTabGroupGestureModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: MatTabGroupGestureModule, declarations: [MatTabGroupGestureDirective], exports: [MatTabGroupGestureDirective] });
MatTabGroupGestureModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MatTabGroupGestureModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MatTabGroupGestureModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        MatTabGroupGestureDirective
                    ],
                    imports: [],
                    exports: [
                        MatTabGroupGestureDirective
                    ]
                }]
        }] });

/*
 * Public API Surface of mat-tab-group-gesture
 */

/**
 * Generated bundle index. Do not edit.
 */

export { MatTabGroupGestureDirective, MatTabGroupGestureModule };
